import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeroBannerDesktop from "../../assets/img/HeroImage.png";
import searchIcon from "../../assets/icon/search.svg";

import { searchProduct } from "../../api/productApi";
import ProductCard from "../products/productCard";

interface SearchResultProduct {
  id: number;
  productName: string;
  image: string | null;
  category: string;
}

export default function ProductSearchPage() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<SearchResultProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);

  // Debounce: baru hit API 400ms setelah user berhenti ngetik. Kalau
  // query kosong, TIDAK fetch sama sekali -- biar halaman ini beneran
  // butuh keyword dulu, tidak tumpang tindih sama halaman katalog biasa
  // (productPage.tsx) yang emang tempatnya browse semua produk.
  useEffect(() => {
    if (query.trim().length === 0) {
      setProducts([]);
      setTotalData(0);
      return;
    }

    const timeout = setTimeout(() => {
      fetchProducts(query);
    }, 400);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchProducts = async (q: string) => {
    try {
      setLoading(true);

      const response = await searchProduct({ q, page: 1, limit: 12 });
      const result = response.data ?? response;

      const mapped: SearchResultProduct[] = (result.productList ?? []).map(
        (p: any) => ({
          id: p.id,
          productName: p.productName,
          image: p.image,
          category: p.category,
        })
      );

      setProducts(mapped);
      setTotalData(result.pagination?.totalData ?? mapped.length);
    } catch (error) {
      console.error(error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (id: number) => {
    navigate(`/product/${id}`);
  };

  return (
    <section className="bg-white min-h-screen">
      {/* HERO */}
      <div className="relative h-[600px] overflow-hidden">
        {/* Background Image */}
        <img
          src={HeroBannerDesktop}
          alt="Hero Banner"
          className="
            absolute
            inset-0
            w-full
            h-full
            object-cover
          "
        />

        {/* Overlay */}
        <div
          className="
            absolute
            inset-0
            bg-gradient-to-r
            from-[#355260]/85
            via-[#63BEC2]/40
            to-transparent
          "
        />

        {/* Content */}
        <div
          className="
            relative
            z-10
            h-full

            max-w-7xl
            mx-auto

            px-8

            flex
            items-center
          ">
          <div className="max-w-[500px]">
            <h1
              className="
                text-white
                text-6xl
                font-bold
                leading-tight
              ">
              Search Product
            </h1>

            <p
              className="
                mt-6
                text-white
                text-xl
                leading-relaxed
              ">
              Cari berbagai kebutuhan seal product terbaik di search engine
              Arunika Medina Pratama.
            </p>

            {/* Search */}
            <div
              className="
                mt-10

                flex
                items-center

                bg-white

                rounded-full

                px-6
                py-4

                shadow-xl
              ">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="search product by name"
                className="
                  flex-1
                  outline-none
                  text-[#324A5A]
                "
              />

              <button
                onClick={() => query.trim() && fetchProducts(query)}
                aria-label="Search">
                <img src={searchIcon} alt="search" className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RESULT SECTION */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        {query.trim().length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#ECECEC]">
              <img src={searchIcon} alt="" className="h-7 w-7 opacity-40" />
            </div>
            <p className="text-lg font-medium text-[#324A5A]">
              Ketik nama produk yang ingin kamu cari
            </p>
            <p className="mt-1 text-sm text-gray-400">
              Misalnya "seal", "piston", atau "hydraulic"
            </p>
          </div>
        )}

        {query.trim().length > 0 && loading && (
          <p className="text-center text-gray-400">Loading products...</p>
        )}

        {query.trim().length > 0 && !loading && products.length === 0 && (
          <p className="text-center text-gray-400">
            No products found for "{query}"
          </p>
        )}

        {query.trim().length > 0 && !loading && products.length > 0 && (
          <>
            <p className="mb-8 text-sm text-gray-500">
              {totalData} product{totalData !== 1 ? "s" : ""} found
            </p>

            <div
              className="
                grid
                grid-cols-2
                gap-4

                sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]
                sm:gap-6

                lg:gap-8
              ">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  title={product.productName}
                  subtitle={product.category}
                  image={product.image}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
